import authService from '../utils/authService';
import config from '../config';

class AdminApiService {
  constructor() {
    this.baseUrl = `${config.API_BASE_URL}/api/admin/dashboard`;
    this.agentManagementUrl = `${config.API_BASE_URL}/api/admin/agent-management`;
    this.supportManagementUrl = `${config.API_BASE_URL}/api/admin/support-management`;
    this.agentTicketsUrl = `${config.API_BASE_URL}/api/admin/agent-tickets`;
    this.supportTicketsUrl = `${config.API_BASE_URL}/api/admin/support-tickets`;
    this.mySupportTicketsUrl = `${config.API_BASE_URL}/api/admin/my-support-tickets`;
    this.bookingDetailsUrl = `${config.API_BASE_URL}/api/admin/booking-details`;
    this.cancellationBookingUrl = `${config.API_BASE_URL}/api/admin/cancellation-booking`;
    this.paymentInfoUrl = `${config.API_BASE_URL}/api/admin/payment-info`;
    this.manualPaymentUrl = `${config.API_BASE_URL}/api/admin/manual-payment`;
    this.paymentRefundInfoUrl = `${config.API_BASE_URL}/api/admin/payment-refund-info`;
    this.settingsUrl = `${config.API_BASE_URL}/api/admin/settings`;
    this.agentTokenRechargeUrl = `${config.API_BASE_URL}/api/admin/agent-token-recharge`;
    
    // Explicitly bind all methods to ensure 'this' context is preserved
    this.makeApiCall = this.makeApiCall.bind(this);
    this.makeAgentApiCall = this.makeAgentApiCall.bind(this);
    this.makeSupportApiCall = this.makeSupportApiCall.bind(this);
    this.makeAgentTicketsApiCall = this.makeAgentTicketsApiCall.bind(this);
    this.makeSupportTicketsApiCall = this.makeSupportTicketsApiCall.bind(this);
    this.makeMySupportTicketsApiCall = this.makeMySupportTicketsApiCall.bind(this);
    this.makeBookingDetailsApiCall = this.makeBookingDetailsApiCall.bind(this);
    this.makeCancellationBookingApiCall = this.makeCancellationBookingApiCall.bind(this);
    this.makePaymentInfoApiCall = this.makePaymentInfoApiCall.bind(this);
    this.makeManualPaymentApiCall = this.makeManualPaymentApiCall.bind(this);
    this.makePaymentRefundInfoApiCall = this.makePaymentRefundInfoApiCall.bind(this);
    this.makeSettingsApiCall = this.makeSettingsApiCall.bind(this);
    this.makeAgentTokenRechargeApiCall = this.makeAgentTokenRechargeApiCall.bind(this);
    this.getDashboard = this.getDashboard.bind(this);
    this.getSearchHotel = this.getSearchHotel.bind(this);
    this.searchHotels = this.searchHotels.bind(this);
    this.getHotelDetails = this.getHotelDetails.bind(this);
    this.getHotelImages = this.getHotelImages.bind(this);
    
    // Agent Management methods
    this.getAgents = this.getAgents.bind(this);
    this.getAgentById = this.getAgentById.bind(this);
    this.updateAgent = this.updateAgent.bind(this);
    this.approveAgent = this.approveAgent.bind(this);
    this.disapproveAgent = this.disapproveAgent.bind(this);
    this.activateAgent = this.activateAgent.bind(this);
    this.deactivateAgent = this.deactivateAgent.bind(this);
    this.getAgentWalletTransactions = this.getAgentWalletTransactions.bind(this);
    
    // Support Management methods
    this.getSupportUsers = this.getSupportUsers.bind(this);
    this.getSupportUserById = this.getSupportUserById.bind(this);
    this.approveSupportUser = this.approveSupportUser.bind(this);
    this.disapproveSupportUser = this.disapproveSupportUser.bind(this);
    this.activateSupportUser = this.activateSupportUser.bind(this);
    this.deactivateSupportUser = this.deactivateSupportUser.bind(this);
    this.addSupportExecutive = this.addSupportExecutive.bind(this);
    
    // Agent Tickets methods
    this.getAgentTickets = this.getAgentTickets.bind(this);
    this.getAgentTicketById = this.getAgentTicketById.bind(this);
    this.assignTicket = this.assignTicket.bind(this);
    this.getSupportAgents = this.getSupportAgents.bind(this);
    
    // Support Tickets methods
    this.getSupportTickets = this.getSupportTickets.bind(this);
    this.getSupportTicketById = this.getSupportTicketById.bind(this);
    this.addCommentToSupportTicket = this.addCommentToSupportTicket.bind(this);
    this.getSupportTicketStatuses = this.getSupportTicketStatuses.bind(this);
    
    // My Support Tickets methods
    this.getMyAssignedTickets = this.getMyAssignedTickets.bind(this);
    this.getMyAssignedTicketById = this.getMyAssignedTicketById.bind(this);
    this.getMyAssignedTicketStatuses = this.getMyAssignedTicketStatuses.bind(this);
    
    // Settings methods
    this.getAdminSettings = this.getAdminSettings.bind(this);
    this.getApiManager = this.getApiManager.bind(this);
    this.addApi = this.addApi.bind(this);
    this.toggleApiStatus = this.toggleApiStatus.bind(this);
    this.editApi = this.editApi.bind(this);
    this.getPlatformSettings = this.getPlatformSettings.bind(this);
    this.updatePlatformSettings = this.updatePlatformSettings.bind(this);
    this.getCancellationPolicySettings = this.getCancellationPolicySettings.bind(this);
    this.updateCancellationPolicySettings = this.updateCancellationPolicySettings.bind(this);
    
    // Agent Token Recharge methods
    this.getAllAgentTokenRechargeDetails = this.getAllAgentTokenRechargeDetails.bind(this);
    this.getAgentBookingHistory = this.getAgentBookingHistory.bind(this);
    this.processTokenRecharge = this.processTokenRecharge.bind(this);
    this.getAgentDetails = this.getAgentDetails.bind(this);
    this.getTokenRechargeStatistics = this.getTokenRechargeStatistics.bind(this);
    
    console.log('👑 AdminApiService initialized');
  }

  // Helper method to make authenticated API calls
  async makeApiCall(endpoint, options = {}) {
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeader()
      },
      credentials: 'include',
      ...options
    };

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, defaultOptions);
      
      if (response.status === 401) {
        // Token expired or invalid
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Admin API call failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Helper method for agent management API calls
  async makeAgentApiCall(endpoint, options = {}) {
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeader()
      },
      credentials: 'include',
      ...options
    };

    try {
      const response = await fetch(`${this.agentManagementUrl}${endpoint}`, defaultOptions);
      
      if (response.status === 401) {
        // Token expired or invalid
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Agent Management API call failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Dashboard APIs
  async getDashboard() {
    return this.makeApiCall('');
  }

  async getSearchHotel() {
    return this.makeApiCall('/searchhotel');
  }

  // POST APIs for actions
  async searchHotels(searchData) {
    return this.makeApiCall('/searchhotel', {
      method: 'POST',
      body: JSON.stringify(searchData)
    });
  }

  // Hotel Details APIs (shared with Agent)
  async getHotelDetails(hotelId, provider, searchId) {
    const endpoint = `/api/hotel-details/${hotelId}?provider=${encodeURIComponent(provider)}&searchId=${encodeURIComponent(searchId)}`;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Hotel details API call failed:`, error);
      throw error;
    }
  }

  async getHotelImages(hotelId, provider) {
    const endpoint = `/api/hotel-details/${hotelId}/images?provider=${encodeURIComponent(provider)}`;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Hotel images API call failed:`, error);
      throw error;
    }
  }

  // Agent Management APIs
  async getAgents(status = true) {
    return this.makeAgentApiCall(`/agents?status=${status}`);
  }

  async getAgentById(id) {
    return this.makeAgentApiCall(`/agents/${id}`);
  }

  async updateAgent(id, agentData) {
    return this.makeAgentApiCall(`/agents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(agentData)
    });
  }

  async approveAgent(id) {
    return this.makeAgentApiCall(`/agents/${id}/approve`, {
      method: 'POST'
    });
  }

  async disapproveAgent(id) {
    return this.makeAgentApiCall(`/agents/${id}/disapprove`, {
      method: 'POST'
    });
  }

  async activateAgent(id) {
    return this.makeAgentApiCall(`/agents/${id}/activate`, {
      method: 'POST'
    });
  }

  async deactivateAgent(id) {
    return this.makeAgentApiCall(`/agents/${id}/deactivate`, {
      method: 'POST'
    });
  }

  async getAgentWalletTransactions(email, id, months = 12) {
    return this.makeAgentApiCall(`/agents/${email}/wallet-transactions?id=${id}&months=${months}`);
  }

  // Helper method to make authenticated Support Management API calls
  async makeSupportApiCall(endpoint, options = {}) {
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeader()
      },
      credentials: 'include',
      ...options
    };

    try {
      const response = await fetch(`${this.supportManagementUrl}${endpoint}`, defaultOptions);
      
      if (response.status === 401) {
        // Token expired or invalid
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Support Management API call failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Support Management APIs
  async getSupportUsers(status = true) {
    return this.makeSupportApiCall(`/users?status=${status}`);
  }

  async getSupportUserById(id) {
    return this.makeSupportApiCall(`/users/${id}`);
  }

  async approveSupportUser(id) {
    return this.makeSupportApiCall(`/users/${id}/approve`, {
      method: 'POST'
    });
  }

  async disapproveSupportUser(id) {
    return this.makeSupportApiCall(`/users/${id}/disapprove`, {
      method: 'POST'
    });
  }

  async activateSupportUser(id) {
    return this.makeSupportApiCall(`/users/${id}/activate`, {
      method: 'POST'
    });
  }

  async deactivateSupportUser(id) {
    return this.makeSupportApiCall(`/users/${id}/deactivate`, {
      method: 'POST'
    });
  }

  // Add a new support executive (POST /api/admin/support-management/add)
  async addSupportExecutive(data) {
    return this.makeSupportApiCall('/add', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // Helper method to make authenticated Agent Tickets API calls
  async makeAgentTicketsApiCall(endpoint, options = {}) {
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeader()
      },
      credentials: 'include',
      ...options
    };

    try {
      const response = await fetch(`${this.agentTicketsUrl}${endpoint}`, defaultOptions);
      
      if (response.status === 401) {
        // Token expired or invalid
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Agent Tickets API call failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Agent Tickets APIs
  async getAgentTickets() {
    return this.makeAgentTicketsApiCall('');
  }

  async getAgentTicketById(ticketId) {
    return this.makeAgentTicketsApiCall(`/${ticketId}`);
  }

  async assignTicket(ticketId, assignedTo) {
    return this.makeAgentTicketsApiCall('/assign-ticket', {
      method: 'POST',
      body: JSON.stringify({
        ticketId: ticketId,
        assignedTo: assignedTo
      })
    });
  }

  async getSupportAgents() {
    return this.makeAgentTicketsApiCall('/support-agents');
  }

  // Helper method to make authenticated Support Tickets API calls
  async makeSupportTicketsApiCall(endpoint, options = {}) {
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeader()
      },
      credentials: 'include',
      ...options
    };

    try {
      const response = await fetch(`${this.supportTicketsUrl}${endpoint}`, defaultOptions);
      
      if (response.status === 401) {
        // Token expired or invalid
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Support Tickets API call failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Support Tickets APIs
  async getSupportTickets(statuses = null) {
    let endpoint = '';
    if (statuses && statuses.length > 0) {
      const statusParams = statuses.map(status => `status=${encodeURIComponent(status)}`).join('&');
      endpoint = `?${statusParams}`;
    }
    return this.makeSupportTicketsApiCall(endpoint);
  }

  async getSupportTicketById(ticketId) {
    return this.makeSupportTicketsApiCall(`/${ticketId}`);
  }

  async addCommentToSupportTicket(ticketId, comment) {
    return this.makeSupportTicketsApiCall(`/${ticketId}/comments`, {
      method: 'POST',
      body: JSON.stringify({
        comment: comment
      })
    });
  }

  async getSupportTicketStatuses() {
    return this.makeSupportTicketsApiCall('/statuses');
  }

  // Helper method to make authenticated My Support Tickets API calls
  async makeMySupportTicketsApiCall(endpoint, options = {}) {
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeader()
      },
      credentials: 'include',
      ...options
    };

    try {
      const response = await fetch(`${this.mySupportTicketsUrl}${endpoint}`, defaultOptions);
      
      if (response.status === 401) {
        // Token expired or invalid
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`My Support Tickets API call failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // My Support Tickets APIs
  async getMyAssignedTickets(statuses = null) {
    let endpoint = '';
    if (statuses && statuses.length > 0) {
      const statusParams = statuses.map(status => `status=${encodeURIComponent(status)}`).join('&');
      endpoint = `?${statusParams}`;
    }
    return this.makeMySupportTicketsApiCall(endpoint);
  }

  async getMyAssignedTicketById(ticketId) {
    return this.makeMySupportTicketsApiCall(`/${ticketId}`);
  }

  async getMyAssignedTicketStatuses() {
    return this.makeMySupportTicketsApiCall('/statuses');
  }

  async addCommentToMyAssignedTicket(ticketId, comment) {
    return this.makeMySupportTicketsApiCall(`/${ticketId}/comments`, {
      method: 'POST',
      body: JSON.stringify({
        comment: comment
      })
    });
  }

  // Helper method to make authenticated Booking Details API calls
  async makeBookingDetailsApiCall(endpoint, options = {}) {
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeader()
      },
      credentials: 'include',
      ...options
    };

    try {
      const response = await fetch(`${this.bookingDetailsUrl}${endpoint}`, defaultOptions);
      
      if (response.status === 401) {
        // Token expired or invalid
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Booking Details API call failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Booking Details APIs
  async getAllBookingDetails() {
    return this.makeBookingDetailsApiCall('');
  }

  async getBookingDetailsById(bookingId) {
    return this.makeBookingDetailsApiCall(`/${bookingId}`);
  }

  // Helper method to make authenticated Cancellation Booking API calls
  async makeCancellationBookingApiCall(endpoint, options = {}) {
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeader()
      },
      credentials: 'include',
      ...options
    };

    try {
      const response = await fetch(`${this.cancellationBookingUrl}${endpoint}`, defaultOptions);
      
      if (response.status === 401) {
        // Token expired or invalid
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Cancellation Booking API call failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Cancellation Booking APIs
  async getAllCancellationBookings() {
    return this.makeCancellationBookingApiCall('');
  }

  async getCancellationBookingById(cancellationId) {
    return this.makeCancellationBookingApiCall(`/${cancellationId}`);
  }

  // Helper method to make authenticated Payment Info API calls
  async makePaymentInfoApiCall(endpoint, options = {}) {
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeader()
      },
      credentials: 'include',
      ...options
    };

    try {
      const response = await fetch(`${this.paymentInfoUrl}${endpoint}`, defaultOptions);
      
      if (response.status === 401) {
        // Token expired or invalid
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Payment Info API call failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Payment Info APIs
  async getAllPaymentInfo() {
    return this.makePaymentInfoApiCall('');
  }

  async getPaymentInfoByAgentId(agentId) {
    return this.makePaymentInfoApiCall(`/${agentId}`);
  }

  // Helper method to make authenticated Manual Payment API calls
  async makeManualPaymentApiCall(endpoint, options = {}) {
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeader()
      },
      credentials: 'include',
      ...options
    };

    try {
      const response = await fetch(`${this.manualPaymentUrl}${endpoint}`, defaultOptions);
      
      if (response.status === 401) {
        // Token expired or invalid
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Manual Payment API call failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Manual Payment APIs
  async getAllManualPayments() {
    return this.makeManualPaymentApiCall('');
  }

  async getManualPaymentByTicketId(ticketId) {
    return this.makeManualPaymentApiCall(`/${ticketId}`);
  }

  async updateTransactionStatus(agentId, transactionId, amount, status) {
    return this.makeManualPaymentApiCall('/update-transaction-status', {
      method: 'POST',
      body: JSON.stringify({
        agentId: agentId,
        transactionId: transactionId,
        amount: amount,
        status: status
      })
    });
  }

  // Helper method to make authenticated Payment Refund Info API calls
  async makePaymentRefundInfoApiCall(endpoint, options = {}) {
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeader()
      },
      credentials: 'include',
      ...options
    };

    try {
      const response = await fetch(`${this.paymentRefundInfoUrl}${endpoint}`, defaultOptions);
      
      if (response.status === 401) {
        // Token expired or invalid
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Payment Refund Info API call failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Payment Refund Info APIs
  async getAllPaymentRefundInfo() {
    return this.makePaymentRefundInfoApiCall('');
  }

  async getPaymentRefundInfoByAgentId(agentId) {
    return this.makePaymentRefundInfoApiCall(`/${agentId}`);
  }

  async getPaymentRefundDetails(refundId) {
    return this.makePaymentRefundInfoApiCall(`/details/${refundId}`);
  }

  // Helper method to make authenticated Settings API calls
  async makeSettingsApiCall(endpoint, options = {}) {
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeader()
      },
      credentials: 'include',
      ...options
    };

    try {
      const response = await fetch(`${this.settingsUrl}${endpoint}`, defaultOptions);
      
      if (response.status === 401) {
        // Token expired or invalid
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Settings API call failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Settings APIs
  async getAdminSettings() {
    return this.makeSettingsApiCall('');
  }

  // API Manager APIs
  async getApiManager() {
    return this.makeSettingsApiCall('/api-manager');
  }

  async addApi(apiData) {
    return this.makeSettingsApiCall('/api-manager/add', {
      method: 'POST',
      body: JSON.stringify(apiData)
    });
  }

  async toggleApiStatus(apiName, status) {
    return this.makeSettingsApiCall('/api-manager/toggle-status', {
      method: 'POST',
      body: JSON.stringify({
        apiName: apiName,
        status: status
      })
    });
  }

  async editApi(apiData) {
    return this.makeSettingsApiCall('/api-manager/edit', {
      method: 'PUT',
      body: JSON.stringify(apiData)
    });
  }

  // Platform Settings APIs
  async getPlatformSettings() {
    return this.makeSettingsApiCall('/platform');
  }

  async updatePlatformSettings(settingsData) {
    return this.makeSettingsApiCall('/platform', {
      method: 'POST',
      body: JSON.stringify(settingsData)
    });
  }

  // Cancellation Policy APIs
  async getCancellationPolicySettings() {
    return this.makeSettingsApiCall('/cancellation-policy');
  }

  async updateCancellationPolicySettings(settingsData) {
    return this.makeSettingsApiCall('/cancellation-policy', {
      method: 'POST',
      body: JSON.stringify(settingsData)
    });
  }

  // Helper method to make authenticated Agent Token Recharge API calls
  async makeAgentTokenRechargeApiCall(endpoint, options = {}) {
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeader()
      },
      credentials: 'include',
      ...options
    };

    try {
      const response = await fetch(`${this.agentTokenRechargeUrl}${endpoint}`, defaultOptions);
      
      if (response.status === 401) {
        // Token expired or invalid
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Agent Token Recharge API call failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Agent Token Recharge APIs
  async getAllAgentTokenRechargeDetails() {
    return this.makeAgentTokenRechargeApiCall('');
  }

  async getAgentBookingHistory(agentId) {
    return this.makeAgentTokenRechargeApiCall(`/${agentId}/booking-history`);
  }

  async processTokenRecharge(agentId, rechargeData) {
    return this.makeAgentTokenRechargeApiCall(`/${agentId}/recharge`, {
      method: 'POST',
      body: JSON.stringify(rechargeData)
    });
  }

  async getAgentDetails(agentId) {
    return this.makeAgentTokenRechargeApiCall(`/${agentId}/details`);
  }

  async getTokenRechargeStatistics() {
    return this.makeAgentTokenRechargeApiCall('/statistics');
  }
}

// Create the service instance
const adminApiServiceInstance = new AdminApiService();

export default adminApiServiceInstance;